/**
 * Resumen de horas y rentabilidad de un proyecto/ticket del CRM.
 *
 * Vive aparte de `crmWorkflow.js` porque es lógica pura: no toca supabase ni el
 * DOM, así que se puede testear con `node --test` sin levantar nada.
 * `crmWorkflow.js` lo reexporta, de modo que los consumidores siguen importando
 * desde ahí.
 */

export function toNumber(value) {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

export function roundNumber(value, digits = 2) {
  const factor = 10 ** digits;
  return Math.round(toNumber(value) * factor) / factor;
}

/** Horas de una carga, venga como `hours_logged` o como `minutes_spent`. */
function entryHours(row) {
  return toNumber(row.hours_logged || (toNumber(row.minutes_spent) / 60));
}

/**
 * Clave YYYY-MM de una carga. `entry_date` es la fecha que declaró el usuario y
 * manda; `created_at` es solo el respaldo para cargas viejas que no la tienen.
 */
export function entryMonthKey(row) {
  const fecha = row?.entry_date || String(row?.created_at || '').slice(0, 10);
  return String(fecha || '').slice(0, 7);
}

/**
 * Cargas de un mes. El presupuesto de un proyecto por abono es MENSUAL, así que
 * medir el consumo contra el acumulado histórico da porcentajes que crecen para
 * siempre: el proyecto del reporte marcaba 189,2% sumando dos meses de cargas
 * contra las 25h de un solo mes.
 *
 * @param {Array} entries
 * @param {string} monthKey  'YYYY-MM'
 */
export function filterEntriesByMonth(entries = [], monthKey) {
  if (!monthKey) return entries;
  return entries.filter((row) => entryMonthKey(row) === monthKey);
}

/* ── Período de medición según el tipo de facturación ──────────────────────────
   El período NO es una preferencia de la empresa: es una propiedad del proyecto.
   Antes lo definía el lógico de organización `crm_distribucion_horas_totales`
   (o todos los proyectos por mes, o todos acumulados), lo cual obliga a elegir
   mal en cuanto la empresa mezcla los dos modelos —que es el caso normal.

     One shot (precio_fijo) → el presupuesto es del proyecto entero: las horas se
                              acumulan desde el inicio y NUNCA se reinician.
     Abono mensual          → el presupuesto son N horas POR MES: se mide el mes
                              en curso y se reinicia el día 1.

   Los tipos legacy en inglés y los discontinuados se leen como One shot, igual
   que ya hacían CrmAdministracion y CrmReportes por su cuenta. Esta es ahora la
   única definición: aquellas copias locales se borraron para que no puedan
   divergir. */

export const BILLING_TYPE_ONE_SHOT = 'precio_fijo';
export const BILLING_TYPE_ABONO_MENSUAL = 'abono_mensual';

export function normalizeBillingType(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'abono_mensual' || normalized === 'retainer') return BILLING_TYPE_ABONO_MENSUAL;
  // 'fixed_price' (legacy) y 'por_horas'/'time_materials' (discontinuados) se
  // leen como One shot para que los proyectos históricos operen sin migración.
  return BILLING_TYPE_ONE_SHOT;
}

/* ── Medición de horas, separada de la facturación ─────────────────────────────
   Atar "las horas se miden por mes" a "se cobra un abono todos los meses" dejó a
   proyectos de tipo Mensual cargados como One shot mostrando el histórico en
   todas las pantallas: el cliente factura por otro lado y no quería el cobro
   recurrente automático, pero sí el cupo de horas mensual.

   `crm_projects.hours_period` lo define explícito ('mensual' | 'acumulado').
   NULL (proyectos anteriores a la columna) cae en la regla vieja: abono mensual
   mide el mes, el resto acumula. La facturación sigue gobernada solo por
   `billing_type`; esto cambia únicamente cómo se miden las horas. */
export const HOURS_PERIOD_MENSUAL = 'mensual';
export const HOURS_PERIOD_ACUMULADO = 'acumulado';

export const HOURS_PERIOD_OPTIONS = [
  { value: HOURS_PERIOD_MENSUAL, label: 'Por mes (se reinicia el día 1)' },
  { value: HOURS_PERIOD_ACUMULADO, label: 'Acumuladas desde el inicio' },
];

/** 'mensual' | 'acumulado', o null si el valor no es válido. */
export function normalizeHoursPeriod(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === HOURS_PERIOD_MENSUAL || normalized === HOURS_PERIOD_ACUMULADO) return normalized;
  return null;
}

/** Período en que se miden las horas del proyecto. */
export function resolveHoursPeriod(project) {
  const explicit = normalizeHoursPeriod(project?.hours_period);
  if (explicit) return explicit;
  return normalizeBillingType(project?.billing_type) === BILLING_TYPE_ABONO_MENSUAL
    ? HOURS_PERIOD_MENSUAL
    : HOURS_PERIOD_ACUMULADO;
}

/**
 * Período sugerido al crear un proyecto: el del tipo de proyecto si lo tiene
 * configurado; si no, el que corresponde a la facturación elegida.
 */
export function suggestHoursPeriod(projectType, billingType) {
  return normalizeHoursPeriod(projectType?.hours_period)
    || resolveHoursPeriod({ billing_type: billingType });
}

/**
 * true si el proyecto acumula horas desde el inicio. El nombre quedó de cuando
 * lo decidía la facturación; ahora lo decide `resolveHoursPeriod`.
 */
export function isAccumulatedBillingProject(project) {
  return resolveHoursPeriod(project) === HOURS_PERIOD_ACUMULADO;
}

/**
 * Clave 'YYYY-MM' del mes en curso, en hora LOCAL. Con `toISOString()` el 31 de
 * diciembre a la noche en Argentina (UTC-3) caía en enero, y el abono del mes se
 * veía vacío justo el último día.
 */
export function currentMonthKey(now = new Date()) {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Período que hay que medirle a un proyecto: `''` (sin filtro = acumulado total)
 * para One shot, 'YYYY-MM' del mes en curso para abono mensual.
 */
export function projectPeriodMonthKey(project, now = new Date()) {
  return isAccumulatedBillingProject(project) ? '' : currentMonthKey(now);
}

/** Cargas que le corresponden al proyecto según su tipo de facturación. */
export function filterEntriesForProject(entries = [], project, now = new Date()) {
  return filterEntriesByMonth(entries, projectPeriodMonthKey(project, now));
}

/**
 * Meses calendario que toca un rango 'YYYY-MM-DD'..'YYYY-MM-DD', ambos inclusive.
 * Un rango dentro del mismo mes cuenta 1; enero a marzo cuenta 3.
 */
export function monthsInRange(fromDate, toDate) {
  const desde = String(fromDate || '').slice(0, 7);
  const hasta = String(toDate || '').slice(0, 7);
  if (!desde || !hasta) return 1;
  const [ya, ma] = desde.split('-').map(Number);
  const [yb, mb] = hasta.split('-').map(Number);
  if (!ya || !ma || !yb || !mb) return 1;
  return Math.max((yb - ya) * 12 + (mb - ma) + 1, 1);
}

/**
 * Presupuesto de horas a comparar contra el consumo de un rango.
 *
 * Solo `budget_hours` es una CUOTA, y en un abono esa cuota es mensual: sobre un
 * rango de 3 meses hay que comparar contra 3 abonos, porque medir 3 meses de
 * cargas contra las horas de uno solo es el mismo error que daba 189,2% de
 * consumo en el detalle. En un One shot la cuota es del proyecto entero y no se
 * escala.
 *
 * `fallbackHours` (la suma de los estimados de las etapas, vía
 * `estimated_total_minutes`) NO se escala nunca, ni siquiera en un abono: es un
 * total de proyecto, no una cuota mensual. Multiplicarlo por los meses del rango
 * inventaría presupuesto — con el preset "Año actual" un abono sano mostraría
 * -90% de desvío.
 *
 * @param {object} project
 * @param {string} fromDate  'YYYY-MM-DD'
 * @param {string} toDate    'YYYY-MM-DD'
 * @param {number} fallbackHours  horas a usar si el proyecto no tiene budget_hours
 */
export function projectBudgetHoursForRange(project, fromDate, toDate, fallbackHours = 0) {
  const cuota = toNumber(project?.budget_hours);
  if (cuota <= 0) return roundNumber(toNumber(fallbackHours), 4);
  if (isAccumulatedBillingProject(project)) return roundNumber(cuota, 4);
  return roundNumber(cuota * monthsInRange(fromDate, toDate), 4);
}

/**
 * @param {Array} entries  cargas de horas (crm_time_entries)
 * @param {number|string} budgetHours  horas presupuestadas/estimadas
 *
 * Sobre las dos métricas de presupuesto: son distintas y se venían confundiendo.
 *   consumptionPct → qué porcentaje del presupuesto se consumió. Siempre >= 0.
 *                    47,3h sobre 25h estimadas = 189,2%.
 *   deviationPct   → cuánto se pasó (o faltó) respecto del presupuesto, con
 *                    signo. El mismo caso = +89,2%. Negativo = por debajo.
 * La card del detalle de proyecto mostraba el primero rotulado como el segundo,
 * y por eso no coincidía con el panel de desvíos de Reportes.
 */
/**
 * Misma cuenta que `summarizeCrmTimeEntries`, pero a partir de totales YA
 * agregados (por ej. por una vista SQL que ya hizo el sum() en el servidor)
 * en vez de reducir un array de `entries` en el cliente. Las dos funciones
 * tienen que dar el mismo resultado para los mismos totales: por eso
 * `summarizeCrmTimeEntries` llama a esta con lo que reduce, en vez de
 * duplicar la fórmula.
 *
 * @param {object} totals  { totalHours, productionCost, generatedValue }
 * @param {number|string} budgetHours
 */
export function summarizeFromTotals(totals = {}, budgetHours = 0) {
  const totalHours = roundNumber(toNumber(totals.totalHours), 4);
  const productionCost = roundNumber(toNumber(totals.productionCost), 2);
  const generatedValue = roundNumber(toNumber(totals.generatedValue), 2);
  const profitabilityAmount = roundNumber(generatedValue - productionCost, 2);
  const profitabilityPct = generatedValue > 0 ? roundNumber((profitabilityAmount / generatedValue) * 100, 2) : 0;
  const normalizedBudgetHours = toNumber(budgetHours);
  const hasBudget = normalizedBudgetHours > 0;

  const consumptionPct = hasBudget
    ? roundNumber((totalHours / normalizedBudgetHours) * 100, 2)
    : 0;
  const deviationPct = hasBudget
    ? roundNumber(((totalHours - normalizedBudgetHours) / normalizedBudgetHours) * 100, 2)
    : 0;

  return {
    totalHours,
    productionCost,
    generatedValue,
    profitabilityAmount,
    profitabilityPct,
    budgetHours: normalizedBudgetHours,
    consumptionPct,
    deviationPct,
    // La alerta mide CONSUMO, no desvío: "vas por el 80% de las horas".
    // Atarla a deviationPct avisaría recién al 180% del presupuesto.
    budgetAlertExceeded: hasBudget && consumptionPct >= 80,
  };
}

export function summarizeCrmTimeEntries(entries = [], budgetHours = 0) {
  const totalHours = entries.reduce((total, row) => total + entryHours(row), 0);
  const productionCost = entries.reduce(
    (total, row) => total + toNumber(row.production_cost_amount || (entryHours(row) * toNumber(row.internal_cost_snapshot))),
    0,
  );
  const generatedValue = entries.reduce(
    (total, row) => total + toNumber(row.generated_value_amount || ((row.is_billable ?? row.billable) ? entryHours(row) * toNumber(row.billing_rate_snapshot) : 0)),
    0,
  );

  return summarizeFromTotals({ totalHours, productionCost, generatedValue }, budgetHours);
}
